/**
 * Route guard requiring the `isAdmin` Firebase Auth custom claim.
 *
 * Used for platform-administrator-only routes (e.g. /dev/*). Reads the
 * `isAdmin` claim via getIdTokenResult() — same plumbing as useTenantId.ts.
 * The claim is minted server-side per PR #968 and is the SaaS-correct way
 * to gate platform-admin actions (replaces the legacy isDirector() username
 * check which conflated platform-admin with per-tenant director authority).
 *
 * Renders:
 *   - 'Checking permissions…' while the auth state + claim resolve
 *   - 'Not available' page for unauthenticated users OR non-admins
 *   - <Outlet /> for admins
 *
 * Distinct from <AuthGuard /> (auth-required, any role) and isDirector()
 * (legacy name-based check being removed per SaaS-readiness audit).
 *
 * @see SaaS-readiness audit (May 2026) — Phase 0 /dev routes lockdown.
 */
import { useEffect, useState } from 'react';
import { Outlet, Link } from 'react-router-dom';
import { auth } from '@/firebase';

type AdminGateState = 'loading' | 'admin' | 'denied';

export function RequireAdmin() {
  const [state, setState] = useState<AdminGateState>('loading');

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setState('denied');
        return;
      }
      try {
        const tokenResult = await user.getIdTokenResult();
        const isAdmin = tokenResult.claims.isAdmin === true;
        setState(isAdmin ? 'admin' : 'denied');
      } catch (err) {
        console.error('[RequireAdmin] Failed to read isAdmin claim:', err);
        setState('denied');
      }
    });
    return unsubscribe;
  }, []);

  if (state === 'loading') {
    return (
      <div className="force-dark min-h-screen bg-brand-dark flex items-center justify-center">
        <div className="text-white/60 text-sm">Checking permissions…</div>
      </div>
    );
  }

  if (state === 'denied') {
    return (
      <div className="force-dark min-h-screen bg-brand-dark text-white flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center space-y-4">
          <h1 className="text-2xl font-light tracking-tight">Not available</h1>
          <p className="text-white/60 text-sm leading-relaxed">
            This area is restricted to platform administrators.
          </p>
          <Link
            to="/"
            className="inline-block mt-4 px-5 py-2 rounded-full bg-white/10 hover:bg-white/20 text-sm transition-colors"
          >
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return <Outlet />;
}
